import React, { useState, useMemo } from 'react'

import {
  Button,
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  string, array, arrayOf, oneOf, shape, number,
} from 'prop-types'

import BrowseAllButton from 'Pods/Shared/BrowseAllButton'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import CategoriesTable from './CategoriesTable'
import CategoryGrid from './CategoryGrid'
import TrendingButtons from './TrendingButtons'
import { getSortedCategoriesWithSubcategories } from '../Services/utils'

const layoutConfig = {
  'asset-category': [
    { index: 0, size: 'wide' },
    { index: 3, size: 'tall' },
  ],
  'print-models-3d': [
    { index: 1, size: 'tall' },
    { index: 5, size: 'wide' },
  ],
}
function AssetCollection({
  id = '',
  title,
  description,
  trendingButtons = EMPTY_ARRAY,
  items,
  browseAllHref,
  browseAllText = 'Browse all models',
  collectionType,
  customWrapperClass = '',
  itemsCount = 10,
  allCategoriesText = 'Show all categories',
  locale = 'en',
}) {
  const [showAll, setShowAll] = useState(false)
  const toggleShowAll = () => setShowAll(!showAll)

  const categoriesToShow = useMemo(() => items.slice(0, itemsCount), [items, itemsCount])

  const gridLayoutConfig = layoutConfig[collectionType] || EMPTY_ARRAY

  // Memoized so the two localeCompare sorts only run when `items` changes,
  // not on every render / "Show all categories" toggle.
  const sortedCategories = useMemo(() => getSortedCategoriesWithSubcategories(items), [items])

  if (!items.length) return null

  return (
    <section id={id} className={`asset-collection ${customWrapperClass} ${collectionType}`}>
      <h2 className="cgt-legacy-heading-h4">{title}</h2>
      <p className="asset-collection__description cgt-legacy-text-default">{description}</p>

      <div className="asset-collection__trending">
        {locale === 'en' && (
          <TrendingButtons
            buttons={trendingButtons}
            buttonType="secondary"
            buttonSize="small"
          />
        )}

        <BrowseAllButton
          className="browse-all-desktop"
          href={browseAllHref}
          text={browseAllText}
        />
      </div>

      <CategoryGrid categories={categoriesToShow} gridLayoutConfig={gridLayoutConfig} className={collectionType} />

      <div className="asset-collection__actions">
        {items.length > itemsCount ? (
          <Button
            type="ghost"
            size="small"
            trailIcon={showAll ? <ChevronUpIcon width="16" height="16" /> : <ChevronDownIcon width="16" height="16" />}
            onClick={toggleShowAll}
            aria-expanded={showAll}
          >
            {allCategoriesText}
          </Button>
        ) : null}

        <BrowseAllButton className="browse-all-mobile" href={browseAllHref} text={browseAllText} />
      </div>

      {items.length > itemsCount && (
        <CategoriesTable
          categories={sortedCategories}
          hidden={!showAll}
        />
      )}
    </section>
  )
}

AssetCollection.propTypes = {
  id: string,
  title: string.isRequired,
  description: string.isRequired,
  trendingButtons: arrayOf(
    shape({
      name: string,
      url: string,
    })
  ),
  itemsCount: number,
  items: array.isRequired,
  browseAllHref: string.isRequired,
  collectionType: oneOf(['asset-category', 'print-models-3d']).isRequired,
  browseAllText: string,
  customWrapperClass: string,
  allCategoriesText: string,
  locale: string,
}

AssetCollection.displayName = 'AssetCollection'

export default AssetCollection
